export interface EmailTemplateData {
  businessName?: string;
  subscriptionName: string;
  customerName?: string;
  currentAmount: number;
  newAmount: number;
  gracePeriodEnd: string;
  changePaymentMethodUrl: string;
  /** New billing date (1st of month) when anchor billing is applied. */
  nextBillingDate?: string;
}

export const generateEmailHTML = (data: EmailTemplateData): string => {
  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const customerName = data.customerName || 'Valued Customer';
  const currentFormatted = formatCurrency(data.currentAmount);
  const newFormatted = formatCurrency(data.newAmount);
  const billingDateHtmlBlock = data.nextBillingDate
    ? `<p style="margin: 0 0 24px 0; font-size: 14px; color: #555; background: #f0f7ff; border: 1px solid #dbeafe; border-radius: 8px; padding: 12px 16px;">
                    Your billing date will move to <strong>${data.nextBillingDate}</strong>, and your subscription will renew on the 1st of each month after that.
                  </p>`
    : '';

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Update your payment method</title>
    </head>
    <body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
      <table role="presentation" style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="padding: 30px 20px;">
            <table role="presentation" style="max-width: 560px; margin: 0 auto; background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,0.08);">

              <tr>
                <td style="background: #f0f0f0; padding: 14px 18px;">
                  <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background: #ff5f57; margin-right: 6px;"></span>
                  <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background: #febc2e; margin-right: 6px;"></span>
                  <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background: #28c840;"></span>
                </td>
              </tr>

              <tr>
                <td style="padding: 20px 28px 0 28px; border-bottom: 1px solid #eee;">
                  <table role="presentation" style="width: 100%; margin-bottom: 16px;">
                    <tr>
                      <td style="padding: 4px 0; font-size: 14px; color: #888; width: 70px;"><strong style="color: #333;">From:</strong></td>
                      <td style="padding: 4px 0; font-size: 14px; color: #444;">support@keepmore.co</td>
                    </tr>
                    <tr>
                      <td style="padding: 4px 0; font-size: 14px; color: #888;"><strong style="color: #333;">Subject:</strong></td>
                      <td style="padding: 4px 0; font-size: 14px; color: #444;">Update your payment method</td>
                    </tr>
                  </table>
                </td>
              </tr>

              <tr>
                <td style="padding: 28px 28px 36px 28px; color: #333; line-height: 1.7;">
                  <p style="margin: 0 0 18px 0; font-size: 18px; font-weight: 700;">Hi ${customerName},</p>

                  <p style="margin: 0 0 28px 0; font-size: 15px; color: #555;">
                    Starting next month, your subscription will include card processing costs. You can avoid these costs by switching to bank transfer (ACH).
                  </p>

                  ${billingDateHtmlBlock}

                  <!-- Preference section -->
                  <p style="margin: 0 0 14px 0; font-size: 15px; font-weight: 600; color: #333;">Choose your preference:</p>

                  <!-- Option 1: Switch to Bank Transfer (selected) -->
                  <table role="presentation" style="width: 100%; border: 2px solid #3b82f6; border-radius: 10px; margin-bottom: 10px; background: #fff;">
                    <tr>
                      <td style="padding: 16px 18px;">
                        <table role="presentation" style="width: 100%;">
                          <tr>
                            <td style="width: 28px; vertical-align: top; padding-top: 2px;">
                              <div style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid #3b82f6; display: flex; align-items: center; justify-content: center;">
                                <div style="width: 10px; height: 10px; border-radius: 50%; background: #3b82f6; margin: 3px auto;"></div>
                              </div>
                            </td>
                            <td style="vertical-align: top;">
                              <div style="font-size: 15px; font-weight: 600; color: #111;">Switch to Bank Transfer</div>
                              <div style="font-size: 13px; color: #999; margin-top: 2px;">No added cost</div>
                            </td>
                            <td style="text-align: right; vertical-align: middle;">
                              <span style="font-size: 16px; font-weight: 600; color: #16a34a;">${currentFormatted}/mo</span>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>

                  <table role="presentation" style="width: 100%; border: 1px solid #e5e7eb; border-radius: 10px; margin-bottom: 28px; background: #fff;">
                    <tr>
                      <td style="padding: 16px 18px;">
                        <table role="presentation" style="width: 100%;">
                          <tr>
                            <td style="width: 28px; vertical-align: middle; padding-top: 2px;">
                              <div style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid #d1d5db;"></div>
                            </td>
                            <td style="vertical-align: middle;">
                              <div style="font-size: 15px; font-weight: 600; color: #111;">Keep Credit Card</div>
                            </td>
                            <td style="text-align: right; vertical-align: middle;">
                              <span style="font-size: 16px; font-weight: 600; color: #333;">${newFormatted}/mo</span>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>

                  <table role="presentation" style="width: 100%;">
                    <tr>
                      <td style="text-align: center;">
                        <a href="${data.changePaymentMethodUrl}" style="display: inline-block; background: #3b82f6; color: #ffffff; padding: 14px 36px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 15px;">Update Payment Method</a>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
};
